/**
 * Holds pushes back until Usercentrics announces consent for Google Analytics,
 * then runs them in the order they were made.
 *
 * The GTM container blocks the GA4 tags while "UserCentrics Google Analytics
 * Optin" reads `false`. This variable also reads `false` before Usercentrics
 * sends any `consent_status`, so the container cannot tell "refused" from
 * "not yet decided". A `pageview` pushed in that window is lost or counted
 * twice (BOOK-257, BOOK-269).
 *
 * This module does not decide if tracking happens. The container does. The
 * module only keeps the push out of the window where the container cannot
 * read the decision.
 *
 * Same logic as `defer-until-consent.ts` in CarSPA.
 */

const CONSENT_STATUS_EVENT = 'consent_status';
/** The Usercentrics data processing service, as it appears in the data layer. */
const GOOGLE_ANALYTICS_SERVICE = 'Google Analytics';

const POLL_INTERVAL_MS = 100;
/**
 * After this time, run the held pushes anyway. The container then blocks them
 * if the visitor has not agreed, so a visitor who is slow with the banner is
 * no worse off than without this module.
 */
const GIVE_UP_AFTER_MS = 30_000;

const deferred: (() => void)[] = [];
let poll: ReturnType<typeof setInterval> | undefined;

/**
 * True when Usercentrics announced a grant at any point. A later withdrawal
 * does not reset it: the container follows the newest `consent_status` and
 * blocks the tag by itself.
 */
const isConsentAnnounced = () =>
  (window.dataLayer ?? []).some(
    entry =>
      entry.event === CONSENT_STATUS_EVENT &&
      entry[GOOGLE_ANALYTICS_SERVICE] === true,
  );

const flush = () => {
  deferred.splice(0).forEach(push => push());
};

const stopPolling = () => {
  clearInterval(poll);
  poll = undefined;
};

export const deferUntilConsent = (push: () => void) => {
  if (typeof window === 'undefined') {
    return;
  }

  if (isConsentAnnounced()) {
    // Held pushes go first. Consent can arrive between two calls inside one
    // poll interval, and the later push must not pass the earlier one.
    stopPolling();
    flush();
    push();
    return;
  }

  deferred.push(push);

  if (poll) {
    return;
  }

  const startedAt = Date.now();
  poll = setInterval(() => {
    if (isConsentAnnounced() || Date.now() - startedAt >= GIVE_UP_AFTER_MS) {
      stopPolling();
      flush();
    }
  }, POLL_INTERVAL_MS);
};

/** Only for tests: forgets the held pushes and stops the poll. */
export const resetDeferUntilConsent = () => {
  stopPolling();
  deferred.length = 0;
};
